import React, { useEffect, useState, useMemo } from 'react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { RbacTreeDiagram, RbacNode } from '../../components/admin/RbacTreeDiagram';

import { SYSTEM_ROLE_THEMES, transformRbacToHierarchy } from '../../utils/rbacUtils';

interface RbacStructure {
    roles: Record<string, string[]>;
}

const RbacVisualizer: React.FC = () => {
    const [structure, setStructure] = useState<RbacStructure | null>(null);
    const [selectedNode, setSelectedNode] = useState<RbacNode | null>(null);
    const [collapsedRoles, setCollapsedRoles] = useState<Set<string>>(new Set());
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRbac = async () => {
            try {
                const data = await authenticatedFetchJSON<RbacStructure>('/api/admin/rbac/structure');
                setStructure(data);
                // Collapse all roles except first 2 by default
                const roleNames = Object.keys(data.roles);
                if (roleNames.length > 2) {
                    setCollapsedRoles(new Set(roleNames.slice(2)));
                }
            } catch (error) {
                console.error("Failed to load RBAC structure:", error);
                // Use fallback mock data for demo
                const fallbackData = {
                    roles: {
                        'magnus': ['*'],
                        'autoGainAdmin': ['platform:manage', 'clients:manage', 'users:manage', 'analytics:view', 'audit:view'],
                        'clientAdmin': ['users:manage', 'projects:manage', 'settings:edit', 'reports:generate'],
                        'projectManager': ['project:read', 'project:edit', 'fmea:approve', 'team:manage'],
                        'engineer': ['fmea:read', 'fmea:write', 'controls:manage', 'dvpr:manage', 'bom:edit'],
                        'viewer': ['fmea:read', 'project:read', 'reports:view']
                    }
                };
                setStructure(fallbackData);
                // Collapse all roles except first 2 by default
                const roleNames = Object.keys(fallbackData.roles);
                if (roleNames.length > 2) {
                    setCollapsedRoles(new Set(roleNames.slice(2)));
                }
            }
        };
        fetchRbac();
    }, []);

    // Transform data into hierarchy
    const hierarchyData = useMemo<RbacNode | null>(() => {
        return transformRbacToHierarchy(structure?.roles, collapsedRoles, 'AutoGain RBAC');
    }, [structure, collapsedRoles]);

    const toggleRole = (roleId: string) => {
        setCollapsedRoles(prev => {
            const next = new Set(prev);
            if (next.has(roleId)) {
                next.delete(roleId);
            } else {
                next.add(roleId);
            }
            return next;
        });
    };

    return (
        <div className="flex flex-col h-full relative">
            <RbacTreeDiagram
                rootNode={hierarchyData}
                roleThemes={SYSTEM_ROLE_THEMES}
                onRoleClick={setSelectedNode}
                onToggleCollapse={toggleRole}
                selectedNodeId={selectedNode?.id}
                glowFilterId="global-rbac-glow"
            />

            {/* Selected Node Panel - Specific to Admin/Global View */}
            {selectedNode && selectedNode.type === 'role' && selectedNode.roleId && structure && (
                <div className="absolute bottom-4 right-4 w-72 bg-slate-800/95 backdrop-blur-sm border border-slate-600 rounded-xl p-4 shadow-2xl z-30 animate-in slide-in-from-right-5 fade-in duration-200">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Users className="text-blue-400" size={20} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-white">{selectedNode.name}</h4>
                            <p className="text-xs text-slate-400">System Role</p>
                        </div>
                    </div>
                    <div className="text-xs text-slate-400 mb-3">
                        <span className="font-medium text-slate-300">Permissions:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {structure.roles[selectedNode.roleId]?.map((p: string) => (
                                <span key={p} className="px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">{p}</span>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={() => navigate(`/admin/permissions?role=${selectedNode.roleId}`)}
                        className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        Edit Permissions
                    </button>
                </div>
            )}
        </div>
    );
};

export default RbacVisualizer;
